import { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Settings2 } from "lucide-react";
import { formatApiError } from "@/auth/AuthContext";
import { parseIso } from "@/lib/dates";
import { inputCls } from "@/components/ChildForm";

const nat = (a, b) => String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
const dayHead = (iso) => { const d = parseIso(iso); return `${d.toLocaleDateString("en-NZ", { weekday: "long" })} ${d.getDate()}`; };
const rangeText = (a, b) => `${parseIso(a).toLocaleDateString("en-NZ", { day: "numeric", month: "short" })} to ${parseIso(b).toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" })}`;
const NONE = "(no classroom recorded)";
const OTHER = "Other classrooms";
const Tick = () => <span className="inline-block h-4 w-4 rounded border border-[var(--hp-border-hi)] align-middle print:border-black" />;
const cell = "border border-[var(--hp-border)] px-2 py-1 print:border-gray-400";
const isBefore = (programme) => /before/i.test(programme || "");

/** What the sheet is made of: for each programme, each school, each area of classrooms, the children booked on each day of the week (a child counted once a day per programme). */
export function buildSheets(data, schools) {
  const days = (data?.days || []).map((d) => d.date);
  const byProg = new Map();
  for (const d of data?.days || []) {
    for (const g of d.groups) {
      for (const r of g.rows) {
        const prog = r.programme || "Bookings";
        const m = byProg.get(prog) || new Map();
        byProg.set(prog, m);
        const school = r.school || "(no school recorded)";
        const day = (m.get(school) || new Map());
        m.set(school, day);
        const list = day.get(d.date) || [];
        day.set(d.date, list);
        if (!list.some((x) => x.id === r.id)) list.push({ id: r.id, name: r.fullName, age: r.age, classroom: r.classroom || "" });
      }
    }
  }
  const order = [...byProg.keys()].sort((a, b) => (isBefore(a) - isBefore(b)) || nat(a, b));
  return order.map((prog) => {
    const m = byProg.get(prog);
    const flat = isBefore(prog);
    const sheets = [...m.keys()].sort(nat).map((school) => {
      const perDay = m.get(school);
      const conf = (schools || []).find((s) => s.name === school);
      const areaOf = new Map();
      for (const a of conf?.pickupAreas || []) for (const c of a.classrooms) areaOf.set(c.toLowerCase(), a.name);
      const seen = new Set();
      for (const list of perDay.values()) for (const c of list) seen.add(c.classroom);
      const areas = [];
      const addArea = (name, rooms) => areas.push({ name, rooms });
      if (flat || !(conf?.pickupAreas || []).length) {
        addArea("", [...seen].filter((c) => c).sort(nat).concat(seen.has("") ? [NONE] : []));
      } else {
        for (const a of conf.pickupAreas) addArea(a.name, a.classrooms);
        const loose = [...seen].filter((c) => c && !areaOf.has(c.toLowerCase())).sort(nat).concat(seen.has("") ? [NONE] : []);
        if (loose.length) addArea(OTHER, loose);
      }
      const names = (day, room, area) => (perDay.get(day) || [])
        .filter((c) => (room === NONE ? !c.classroom : c.classroom.toLowerCase() === room.toLowerCase()))
        .sort((a, b) => nat(a.name, b.name));
      const count = (day, rooms) => rooms.reduce((n, room) => n + names(day, room).length, 0);
      return { school, flat, days, areas: areas.map((a) => ({ ...a, count: Object.fromEntries(days.map((d) => [d, count(d, a.rooms)])), names })), perDay };
    });
    return { programme: prog, flat, sheets };
  });
}

function GroupedTable({ area, days, school, multiSchool, programme, range }) {
  const cols = days.length * 2;
  const rooms = area.rooms;
  return (
    <div className="break-inside-avoid space-y-1" data-testid={`sheet-area-${programme}-${school}-${area.name || "all"}`}>
      <h4 className="font-head text-base font-semibold text-[var(--hp-heading)] print:text-black">{programme} pick up list{area.name ? ` · ${area.name}` : ""}{multiSchool ? ` · ${school}` : ""}<span className="ml-2 text-xs font-normal text-[var(--hp-muted)]">{range}</span></h4>
      <div className="overflow-x-auto"><table className="w-full table-fixed border-collapse text-xs" style={{ minWidth: `${days.length * 150}px` }}>
        <colgroup>{days.flatMap((d) => [<col key={`${d}n`} />, <col key={`${d}t`} style={{ width: "34px" }} />])}</colgroup>
        <thead>
          <tr>{days.map((d) => <th key={d} colSpan={2} className={`${cell} bg-[var(--hp-tint-5)] text-left font-semibold text-[var(--hp-heading)] print:text-black`}>{dayHead(d)}</th>)}</tr>
          <tr>{days.map((d) => <th key={d} colSpan={2} className={`${cell} text-left font-mono-num text-[var(--hp-heading)] print:text-black`} data-testid={`sheet-count-${programme}-${school}-${area.name || "all"}-${d}`}>{area.count[d]}</th>)}</tr>
        </thead>
        <tbody>
          {rooms.length === 0 && <tr><td colSpan={cols} className={`${cell} text-[var(--hp-muted)]`}>Nobody booked.</td></tr>}
          {rooms.map((room) => {
            const per = days.map((d) => area.names(d, room));
            const rows = Math.max(1, ...per.map((x) => x.length));
            return [
              <tr key={`${room}-h`}><td colSpan={cols} className={`${cell} bg-[var(--hp-tint-5)] font-semibold text-[var(--hp-heading)] print:text-black`} data-testid={`sheet-room-${school}-${room}`}>{room}</td></tr>,
              ...Array.from({ length: rows }, (_, i) => (
                <tr key={`${room}-${i}`}>{days.map((d, di) => { const c = per[di][i]; return [
                  <td key={`${d}n`} className={`${cell} truncate text-[var(--hp-heading)] print:text-black`} data-testid={c ? `sheet-name-${d}-${c.id}` : undefined}>{c ? <>{c.name}{c.age != null ? <span className="text-[var(--hp-muted)]"> ({c.age})</span> : null}</> : " "}</td>,
                  <td key={`${d}t`} className={`${cell} w-8 text-center`}>{c ? <Tick /> : null}</td>]; })}</tr>)),
            ];
          })}
        </tbody>
      </table></div>
    </div>
  );
}

function FlatTable({ sheet, programme, range, multiSchool }) {
  const days = sheet.days;
  const per = days.map((d) => (sheet.perDay.get(d) || []).slice().sort((a, b) => nat(a.name, b.name)));
  const rows = Math.max(1, ...per.map((x) => x.length));
  return (
    <div className="break-inside-avoid space-y-1" data-testid={`sheet-flat-${programme}-${sheet.school}`}>
      <h4 className="font-head text-base font-semibold text-[var(--hp-heading)] print:text-black">{programme} list{multiSchool ? ` · ${sheet.school}` : ""}<span className="ml-2 text-xs font-normal text-[var(--hp-muted)]">{range}</span></h4>
      <div className="overflow-x-auto"><table className="w-full table-fixed border-collapse text-xs" style={{ minWidth: `${days.length * 200}px` }}>
        <colgroup>{days.flatMap((d) => [<col key={`${d}n`} />, <col key={`${d}r`} style={{ width: "44px" }} />, <col key={`${d}t`} style={{ width: "48px" }} />])}</colgroup>
        <thead>
          <tr>{days.map((d) => <th key={d} colSpan={3} className={`${cell} bg-[var(--hp-tint-5)] text-left font-semibold text-[var(--hp-heading)] print:text-black`}>{dayHead(d)}</th>)}</tr>
          <tr>{days.map((d, di) => [
            <th key={`${d}c`} className={`${cell} text-left font-mono-num text-[var(--hp-heading)] print:text-black`} data-testid={`sheet-count-${programme}-${sheet.school}-all-${d}`}>{per[di].length}</th>,
            <th key={`${d}r`} className={`${cell} w-12 text-left font-normal uppercase tracking-wider text-[var(--hp-muted)]`}>RM</th>,
            <th key={`${d}t`} className={`${cell} w-12 text-center font-normal uppercase tracking-wider text-[var(--hp-muted)]`}>Tick in</th>])}</tr>
        </thead>
        <tbody>{Array.from({ length: rows }, (_, i) => (
          <tr key={i}>{days.map((d, di) => { const c = per[di][i]; return [
            <td key={`${d}n`} className={`${cell} truncate text-[var(--hp-heading)] print:text-black`} data-testid={c ? `sheet-name-${d}-${c.id}` : undefined}>{c ? <>{c.name}{c.age != null ? <span className="text-[var(--hp-muted)]"> ({c.age})</span> : null}</> : " "}</td>,
            <td key={`${d}r`} className={`${cell} truncate text-[var(--hp-muted)] print:text-black`}>{c?.classroom || ""}</td>,
            <td key={`${d}t`} className={`${cell} text-center`}>{c ? <Tick /> : null}</td>]; })}</tr>))}</tbody>
      </table></div>
    </div>
  );
}

/** The weekly pick-up sheet, laid out the way the paper one is: a table for each area of classrooms (Room 1-10, Studios down stairs...), Monday to Friday across the page, a count at the top of each day, a band for each classroom with that day's children under it and a box to tick them in. Before school is a plain list with the room beside each name. */
export function PickupSheet({ data, schools }) {
  const sheets = useMemo(() => buildSheets(data, schools), [data, schools]);
  const days = (data?.days || []).map((d) => d.date);
  const range = rangeText(data.weekStart, data.weekEnd);
  return (
    <div className="space-y-6" data-testid="pickup-sheet">
      <style media="print">{"@page { size: landscape; margin: 10mm; }"}</style>
      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
        {days.map((d) => <span key={d} className="text-xs text-[var(--hp-muted)]">{dayHead(d)} <b className="font-mono-num text-[var(--hp-heading)] print:text-black" data-testid={`sheet-day-total-${d}`}>{data.days.find((x) => x.date === d)?.total ?? 0}</b></span>)}
      </div>
      {sheets.map((p) => {
        const multi = p.sheets.length > 1;
        return (
          <section key={p.programme} className="space-y-4" data-testid={`sheet-programme-${p.programme}`}>
            {p.sheets.map((sh) => p.flat
              ? <FlatTable key={sh.school} sheet={sh} programme={p.programme} range={range} multiSchool={multi} />
              : sh.areas.map((a) => <GroupedTable key={`${sh.school}-${a.name}`} area={a} days={days} school={sh.school} multiSchool={multi} programme={p.programme} range={range} />))}
          </section>);
      })}
    </div>
  );
}

/** Where the sheet's areas are set up: for each school, name an area and choose which classrooms it holds. */
export function PickupAreas({ api, schools, data, onChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const [edits, setEdits] = useState({});            // school id -> areas being edited
  const seen = useMemo(() => {
    const m = {};
    for (const d of data?.days || []) for (const g of d.groups) for (const r of g.rows) if (r.classroom) (m[r.school] = m[r.school] || new Set()).add(r.classroom);
    return m;
  }, [data]);
  const areasOf = (s) => edits[s.id] ?? (s.pickupAreas || []);
  const set = (s, next) => setEdits((e) => ({ ...e, [s.id]: next }));
  const rooms = (s) => [...new Set([...(s.classrooms || []), ...(seen[s.name] || [])])].sort(nat);
  const toggle = (s, ai, room) => {
    const cur = areasOf(s).map((a) => ({ ...a, classrooms: a.classrooms.filter((c) => c.toLowerCase() !== room.toLowerCase()) }));
    const had = areasOf(s)[ai].classrooms.some((c) => c.toLowerCase() === room.toLowerCase());
    if (!had) cur[ai].classrooms = [...cur[ai].classrooms, room];
    set(s, cur);
  };
  const save = async (s) => {
    setBusy(s.id);
    try { await axios.put(`${api}/schools/${s.id}`, { name: s.name, pickupAreas: areasOf(s).filter((a) => a.name.trim()) }); setEdits((e) => { const { [s.id]: _x, ...rest } = e; return rest; }); toast.success(`${s.name}: areas saved`); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(null); }
  };
  return (
    <div className="rounded-xl border border-[var(--hp-border)] p-3 print:hidden" data-testid="pickup-areas">
      <button data-testid="pickup-areas-toggle" aria-expanded={open} onClick={() => setOpen(!open)} className="inline-flex items-center gap-2 text-xs font-semibold text-[var(--hp-heading)]"><Settings2 className="h-3.5 w-3.5" /> Set up the sheet's areas (for example "Room 1-10", "Studios down stairs")</button>
      {open && (
        <div className="mt-3 space-y-4">
          <p className="text-xs text-[var(--hp-muted)]">An area groups classrooms into one table on the sheet. Each school has its own. A classroom that isn't in an area is listed under "Other classrooms". With no areas set up, the sheet is one table for the school.</p>
          {schools.length === 0 && <p className="text-xs text-[var(--hp-muted)]">No schools yet — they appear as children are added.</p>}
          {schools.map((s) => (
            <div key={s.id} className="space-y-2 rounded-xl border border-[var(--hp-border)] p-3" data-testid={`pickup-areas-school-${s.name}`}>
              <p className="text-sm font-semibold text-[var(--hp-heading)]">{s.name}</p>
              {areasOf(s).map((a, ai) => (
                <div key={ai} className="space-y-1.5 rounded-lg bg-[var(--hp-tint-5)] p-2.5" data-testid={`pickup-area-${s.name}-${ai}`}>
                  <div className="flex items-center gap-2">
                    <input aria-label="Area name" data-testid={`pickup-area-name-${s.name}-${ai}`} value={a.name} maxLength={60} placeholder="Area name" onChange={(e) => set(s, areasOf(s).map((x, i) => (i === ai ? { ...x, name: e.target.value } : x)))} className={`${inputCls} flex-1 py-1`} />
                    <button aria-label={`Remove ${a.name || "area"}`} data-testid={`pickup-area-remove-${s.name}-${ai}`} onClick={() => set(s, areasOf(s).filter((_, i) => i !== ai))} className="rounded-full p-1.5 text-[var(--hp-muted)] hover:text-[var(--hp-loss)]"><Trash2 className="h-4 w-4" /></button>
                  </div>
                  <div className="flex flex-wrap gap-1.5">{rooms(s).map((room) => {
                    const here = a.classrooms.some((c) => c.toLowerCase() === room.toLowerCase());
                    const elsewhere = !here && areasOf(s).some((x, i) => i !== ai && x.classrooms.some((c) => c.toLowerCase() === room.toLowerCase()));
                    return <button key={room} type="button" aria-pressed={here} data-testid={`pickup-area-room-${s.name}-${ai}-${room}`} onClick={() => toggle(s, ai, room)} title={elsewhere ? "In another area: tap to move it here" : undefined}
                      className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${here ? "border-[var(--hp-primary)] bg-[var(--hp-primary)]/15 text-[var(--hp-profit)]" : elsewhere ? "border-[var(--hp-border)] text-[var(--hp-muted)] opacity-60" : "border-[var(--hp-border-hi)] text-[var(--hp-heading)]"}`}>{room}</button>; })}
                    {rooms(s).length === 0 && <span className="text-xs text-[var(--hp-muted)]">No classrooms recorded for this school yet.</span>}</div>
                </div>))}
              <div className="flex flex-wrap gap-2">
                <button data-testid={`pickup-area-add-${s.name}`} onClick={() => set(s, [...areasOf(s), { name: "", classrooms: [] }])} className="inline-flex items-center gap-1.5 rounded-full border border-[var(--hp-border-hi)] px-3 py-1.5 text-xs font-semibold text-[var(--hp-heading)] hover:bg-[var(--hp-tint-5)]"><Plus className="h-3.5 w-3.5" /> Add an area</button>
                {edits[s.id] && <button data-testid={`pickup-area-save-${s.name}`} disabled={busy === s.id} onClick={() => save(s)} className="rounded-full bg-[var(--hp-primary)] px-4 py-1.5 text-xs font-bold text-[#04231a] disabled:opacity-40">Save {s.name}</button>}
              </div>
            </div>))}
        </div>)}
    </div>
  );
}
